import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, Loader2Icon, SparklesIcon, FileUpIcon, DollarSignIcon, StarIcon, BriefcaseIcon, XIcon } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { CaseStudyCard } from './CaseStudyCard';
import { QuickFilterPills } from './QuickFilterPills';
import { ExportChatButton } from './ExportChatButton';
import { chatWithPortfolio, generateSuggestedQuestions } from '../utils/chatService';
import { analyzeJobDescription } from '../utils/openaiService';
import { getCaseStudyThumbnail } from '../utils/caseStudyMetadata';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
const CHAT_STORAGE_KEY = 'taio_chat_messages';
const SESSION_KEY = 'taio_chat_session';
// Generate a unique session ID
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
// Check if this is a new session
const isNewSession = (): boolean => {
  const storedSessionId = sessionStorage.getItem(SESSION_KEY);
  if (!storedSessionId) {
    // No session ID means new session
    sessionStorage.setItem(SESSION_KEY, generateSessionId());
    return true;
  }
  return false;
};
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  caseStudyCards?: Array<{
    slug: string;
    title: string;
    relevance: number;
    description: string;
    thumbnail: string;
  }>;
}
interface ChatInterfaceProps {
  onCaseStudyClick: (slug: string) => void;
  isSplitView: boolean;
  activeCaseStudy?: string | null;
  onClose?: () => void; // Add close handler for mobile
}
// Helper function to load messages from localStorage
const loadMessagesFromStorage = (): Message[] => {
  // Check if this is a new session - if so, clear old messages
  if (isNewSession()) {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    return getDefaultWelcomeMessage();
  }
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
  } catch (error) {
    console.error('Error loading chat messages from storage:', error);
  }
  // Return default welcome message if nothing in storage
  return getDefaultWelcomeMessage();
};
// Helper to get default welcome message
const getDefaultWelcomeMessage = (): Message[] => {
  return [{
    id: '1',
    role: 'assistant',
    content: "Hi! I'm TAIO, Mateo's AI portfolio assistant. Ask me anything about his work, skills, or experience. What would you like to know?",
    timestamp: new Date()
  }];
};
// Helper function to save messages to localStorage
const saveMessagesToStorage = (messages: Message[]) => {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
  } catch (error) {
    console.error('Error saving chat messages to storage:', error);
  }
};
export function ChatInterface({
  onCaseStudyClick,
  isSplitView,
  activeCaseStudy,
  onClose
}: ChatInterfaceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    chatEntryPoint,
    setChatEntryPoint,
    closeChatDrawer,
    setPreviousPage
  } = usePortfolio();
  // Load messages from localStorage on mount
  const [messages, setMessages] = useState<Message[]>(loadMessagesFromStorage);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [isRoleMatchMode, setIsRoleMatchMode] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  // Save messages to localStorage whenever they change
  useEffect(() => {
    saveMessagesToStorage(messages);
  }, [messages]);
  // Helper to get case study title
  const getCaseStudyTitle = (slug: string): string => {
    const titles: Record<string, string> = {
      'us-bank': 'US Bank Commercial Card',
      'cyber-security': 'Unisys Stealth',
      'ai-comparison': 'Watson vs Dialogflow',
      'case-western': 'Case Western Reserve University'
    };
    return titles[slug] || slug;
  };
  const caseStudyContext = activeCaseStudy ? getCaseStudyTitle(activeCaseStudy) : null;
  // Smart scroll behavior - FIXED: Prevent page scroll, only scroll chat container
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.id !== lastMessageId) {
      setLastMessageId(lastMessage.id);
      // Small delay to allow DOM to update
      setTimeout(() => {
        if (!messagesContainerRef.current) return;
        if (lastMessage.role === 'user') {
          // For user messages, scroll to bottom to show what they just sent
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          // For assistant messages, scroll to the message within the container
          // CRITICAL: Use container.scrollTo instead of element.scrollIntoView to prevent page scroll
          const messageElement = document.getElementById(`message-${lastMessage.id}`);
          if (messageElement && messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            const messageTop = messageElement.offsetTop;
            const containerScrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            // Calculate if message is already visible
            const messageBottom = messageTop + messageElement.clientHeight;
            const isVisible = messageTop >= containerScrollTop && messageBottom <= containerScrollTop + containerHeight;
            // Only scroll if message is not fully visible
            if (!isVisible) {
              container.scrollTo({
                top: messageTop - 20,
                behavior: 'smooth'
              });
            }
          }
        }
      }, 100);
    }
  }, [messages, lastMessageId]);
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus input
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      // Escape to clear input or exit role match mode
      if (e.key === 'Escape') {
        if (input) {
          setInput('');
        } else if (isRoleMatchMode) {
          setIsRoleMatchMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [input, isRoleMatchMode]);
  // Handle close - navigate back to entry point
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    // Navigate back to where user entered chat from
    if (chatEntryPoint && chatEntryPoint !== location.pathname) {
      navigate(chatEntryPoint);
    }
    // Clear entry point and close drawer
    setChatEntryPoint(null);
    closeChatDrawer();
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setActiveFilter(null);
    try {
      if (isRoleMatchMode || input.length > 500) {
        const analysisResult = await analyzeJobDescription(input.trim());
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
          timestamp: new Date(),
          caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
            slug: cs.slug,
            title: cs.title,
            relevance: cs.relevance,
            description: cs.reason,
            thumbnail: getCaseStudyThumbnail(cs.slug)
          }))
        };
        setMessages(prev => [...prev, assistantMessage]);
        setIsRoleMatchMode(false);
      } else {
        // Regular chat - NOW WITH ACTIVE CASE STUDY CONTEXT
        const response = await chatWithPortfolio(input.trim(), messages, activeCaseStudy);
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          caseStudyCards: response.caseStudyCards
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "I'm having trouble processing that. Could you try rephrasing?",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };
  const handleFilterClick = (query: string) => {
    setInput(query);
    setActiveFilter(query);
    // Auto-submit the filter query WITHOUT scrolling
    setTimeout(() => {
      // Prevent default form submission scroll behavior
      const form = inputRef.current?.form;
      if (form) {
        // Create a custom event that won't trigger scroll
        const submitEvent = new Event('submit', {
          cancelable: true,
          bubbles: true
        });
        form.dispatchEvent(submitEvent);
      }
    }, 100);
  };
  // Handle Role Match button click - send instructions to chat
  const handleRoleMatchClick = () => {
    const instructionMessage: Message = {
      id: Date.now().toString(),
      role: 'assistant',
      content: "**Role Match Mode**\n\nPaste the job description into the chat, and I'll analyze how Mateo's skills and experience align with the role. I'll highlight relevant case studies and provide a detailed match assessment.",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, instructionMessage]);
    setIsRoleMatchMode(true);
  };
  // Handle Upload JD button click - directly trigger file input (no chat message)
  const handleUploadJDClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    setIsRoleMatchMode(true);
    try {
      const text = await file.text();
      setInput(text);
      // Auto-analyze the uploaded file
      const analysisResult = await analyzeJobDescription(text);
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: `[Uploaded job description: ${file.name}]`,
        timestamp: new Date()
      };
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I analyzed the job description for "${analysisResult.jobTitle}". Here's how Mateo's experience matches:\n\n${analysisResult.summary}`,
        timestamp: new Date(),
        caseStudyCards: analysisResult.relevantCaseStudies.map(cs => ({
          slug: cs.slug,
          title: cs.title,
          relevance: cs.relevance,
          description: cs.reason,
          thumbnail: getCaseStudyThumbnail(cs.slug)
        }))
      };
      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setInput('');
      setIsRoleMatchMode(false);
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const handleCaseStudyCardClick = (slug: string) => {
    console.log('✅ ChatInterface: Case study card clicked:', slug);
    // CRITICAL: Reset scroll position IMMEDIATELY before navigation
    window.scrollTo(0, 0);
    // CRITICAL: Store current page before navigating to case study
    setPreviousPage(location.pathname);
    console.log('📍 Stored previous page:', location.pathname);
    console.log('✅ Calling onCaseStudyClick with slug:', slug);
    onCaseStudyClick(slug);
  };
  const suggestions = generateSuggestedQuestions(messages);
  return <div className={`flex flex-col h-full ${isSplitView ? 'hidden lg:flex' : 'flex'}`} role="region" aria-label="Chat interface" style={{
    position: 'relative',
    zIndex: 99999999
  }} data-id="element-144">
      {/* Mobile-Only Header with Close Button */}
      {onClose && <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/95 backdrop-blur-sm flex-shrink-0" data-id="element-145">
          <div className="flex items-center gap-2" data-id="element-146">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-147">
              <SparklesIcon className="w-4 h-4 chat-sparkles-icon" aria-hidden="true" data-id="element-148" />
            </div>
            <h2 className="text-white font-medium text-base" data-id="element-149">Chat with TAIO</h2>
          </div>
          <button onClick={handleClose} className="w-11 h-11 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Close chat" data-id="element-150">
            <XIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-151" />
          </button>
        </div>}

      {/* Quick Prompts Section - NOW VISIBLE ON MOBILE */}
      <div className="px-4 pt-4 flex-shrink-0" data-id="element-152">
        <p className="text-white/60 text-xs uppercase tracking-wide mb-2" data-id="element-153">
          Quick Prompts:
        </p>
        <div className="grid grid-cols-2 gap-2" data-id="element-154">
          <button onClick={() => handleFilterClick("Tell me about Mateo's AI & Innovation work")} className="text-xs px-3 py-3 rounded-lg bg-white/5 text-white/80 border border-white/20 hover:bg-white/10 hover:border-white/30 active:bg-white/15 transition-all text-left min-h-[44px] flex items-center gap-2" aria-label="Ask about AI and Innovation work" data-id="element-155">
            <SparklesIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-156" />
            <span data-id="element-157">AI & Innovation</span>
          </button>
          <button onClick={() => handleFilterClick("Show me Mateo's Finance projects")} className="text-xs px-3 py-3 rounded-lg bg-white/5 text-white/80 border border-white/20 hover:bg-white/10 hover:border-white/30 active:bg-white/15 transition-all text-left min-h-[44px] flex items-center gap-2" aria-label="Show Finance projects" data-id="element-158">
            <DollarSignIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-159" />
            <span data-id="element-160">Finance</span>
          </button>
          <button onClick={() => handleFilterClick("What are Mateo's top skills?")} className="text-xs px-3 py-3 rounded-lg bg-white/5 text-white/80 border border-white/20 hover:bg-white/10 hover:border-white/30 active:bg-white/15 transition-all text-left min-h-[44px] flex items-center gap-2" aria-label="Ask about top skills" data-id="element-161">
            <StarIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-162" />
            <span data-id="element-163">Top Skills</span>
          </button>
          <button onClick={() => handleFilterClick("Tell me about Mateo's experience")} className="text-xs px-3 py-3 rounded-lg bg-white/5 text-white/80 border border-white/20 hover:bg-white/10 hover:border-white/30 active:bg-white/15 transition-all text-left min-h-[44px] flex items-center gap-2" aria-label="Ask about experience" data-id="element-164">
            <BriefcaseIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-165" />
            <span data-id="element-166">Experience</span>
          </button>
        </div>
      </div>

      {/* Messages Area - Improved mobile spacing */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 lg:px-4 py-3 lg:py-6 space-y-3 lg:space-y-6" style={{
      overflowAnchor: 'none',
      scrollBehavior: 'smooth',
      position: 'relative',
      zIndex: 1,
      WebkitOverflowScrolling: 'touch'
    }} role="log" aria-live="polite" aria-atomic="false" data-id="element-167">
        <AnimatePresence initial={false} data-id="element-168">
          {messages.map(message => <div key={message.id} id={`message-${message.id}`} style={{
          position: 'relative',
          zIndex: 1,
          scrollMarginTop: '20px'
        }} data-id="element-169">
              <ChatMessage role={message.role} content={message.content} timestamp={message.timestamp} isStreaming={false} data-id="element-170" />

              {/* Case Study Cards */}
              {message.caseStudyCards && message.caseStudyCards.length > 0 && <div className="mt-4 ml-11 space-y-3" style={{
            position: 'relative',
            zIndex: 100,
            pointerEvents: 'auto'
          }} data-id="element-171">
                  {message.caseStudyCards.map(card => <CaseStudyCard key={card.slug} slug={card.slug} title={card.title} description={card.description} relevance={card.relevance} thumbnail={card.thumbnail} onClick={() => handleCaseStudyCardClick(card.slug)} data-id="element-172" />)}
                </div>}
            </div>)}
        </AnimatePresence>

        {isLoading && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex gap-3" data-id="element-173">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" data-id="element-174">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-175">
                <SparklesIcon className="w-4 h-4 text-white" data-id="element-176" />
              </motion.div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-2" data-id="element-177">
              <motion.div className="flex gap-1" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-178">
                <motion.div className="w-2 h-2 bg-white/60 rounded-full" animate={{
              y: [0, -8, 0],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: 0
            }} data-id="element-179" />
                <motion.div className="w-2 h-2 bg-white/60 rounded-full" animate={{
              y: [0, -8, 0],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: 0.2
            }} data-id="element-180" />
                <motion.div className="w-2 h-2 bg-white/60 rounded-full" animate={{
              y: [0, -8, 0],
              opacity: [0.6, 1, 0.6]
            }} transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: 0.4
            }} data-id="element-181" />
              </motion.div>
              <span className="text-sm text-white/60 ml-2" data-id="element-182">Thinking...</span>
            </div>
          </motion.div>}

        <div ref={messagesEndRef} data-id="element-183" />
      </div>

      {/* Input Area */}
      <div className="border-t border-white/10 p-3 lg:p-4 flex-shrink-0 bg-black/95 backdrop-blur-sm" data-id="element-184">
        {/* Action buttons - Fixed mobile layout with text labels */}
        <div className="flex items-center gap-2 mb-2 overflow-x-auto" data-id="element-185">
          {/* Role Match Button - Now shows full text on mobile */}
          <button onClick={handleRoleMatchClick} className="text-xs px-3 py-2.5 rounded-full bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 active:bg-white/15 transition-all min-h-[44px] flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Start role match mode" data-id="element-186">
            <span className="whitespace-nowrap" data-id="element-187">Role Match</span>
          </button>

          <input ref={fileInputRef} type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" aria-label="Upload job description file" data-id="element-188" />

          {/* Upload JD Button - Now shows text with icon on mobile */}
          <button onClick={handleUploadJDClick} className="text-xs px-3 py-2.5 rounded-full bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 active:bg-white/15 transition-all flex items-center justify-center gap-1.5 min-h-[44px] flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-white/20" aria-label="Upload job description" data-id="element-189">
            <FileUpIcon className="w-4 h-4" aria-hidden="true" data-id="element-190" />
            <span className="whitespace-nowrap" data-id="element-191">Upload JD</span>
          </button>

          <div className="flex-1 min-w-[20px]" data-id="element-192"></div>

          <div className="flex-shrink-0" data-id="element-193">
            <ExportChatButton messages={messages} data-id="element-194" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex gap-2" data-id="element-195">
          <label htmlFor="chat-input" className="sr-only" data-id="element-196">
            Chat message input
          </label>
          <input id="chat-input" ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} placeholder={isRoleMatchMode ? 'Paste job description here...' : 'Ask about my skills or experience...'} disabled={isLoading} autoComplete="off" className="flex-1 bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/70 outline-none focus:border-white/40 focus:ring-2 focus:ring-white/20 transition-colors disabled:opacity-50 text-base" style={{
          fontSize: '16px',
          minHeight: '44px' // Ensure touch target size
        }} aria-label={isRoleMatchMode ? 'Paste job description here' : 'Ask about skills or experience'} data-id="element-197" />
          <button type="submit" disabled={!input.trim() || isLoading} className="flex-shrink-0 w-12 h-12 bg-white hover:bg-white/90 active:bg-white/80 text-black rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/40" aria-label={isLoading ? 'Sending message' : 'Send message'} style={{
          minWidth: '44px',
          minHeight: '44px'
        }} data-id="element-198">
            {isLoading ? <Loader2Icon className="w-5 h-5 animate-spin" aria-hidden="true" data-id="element-199" /> : <SendIcon className="w-5 h-5" aria-hidden="true" data-id="element-200" />}
          </button>
        </form>

        {/* Keyboard shortcuts hint - hidden on mobile */}
        <div className="mt-2 hidden sm:flex items-center justify-center gap-4 text-xs text-white/60" aria-hidden="true" data-id="element-201">
          <span data-id="element-202">⌘K to focus</span>
          <span data-id="element-203">•</span>
          <span data-id="element-204">ESC to clear</span>
        </div>
      </div>
    </div>;
}